import React, { useEffect, useState } from 'react';
import GridSearchTemplate from '../GridSearchTemplate';
import { Rates, Hotels } from '../../api/services/next';
import CheckboxWrapper from '../../components/shared/Checkbox';
import CheckBoxToggle from '../../components/shared/CheckBoxToggle';
import { DatePicker } from '../../components/shared/DatePicker';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import { useTextResources } from '../../language';
import { Button, Checkbox, Form, Icon } from 'semantic-ui-react';
import { dateFormat } from '../../components/shared/constants';
import moment from 'moment';
import RateCategoriesDropDown from '../../components/shared/RateCategoriesDropDown';
import { RateCategory as RateCategories } from '../../components/shared/constants';
import RatesDropDown from '../../components/shared/RatesDropDown';
import { PriceListCategorySelectionPopup } from './PriceListCategorySelectionPopup';
import { Currencies } from '../../components/shared/currencies';
import { onError } from '../../components/shared/messages';
import PriceGroupsDropDown from "../../components/shared/PriceGroupsDropDown";
import ClassificationCategoriesDropDown from "../../components/shared/ClassificationCategoriesDropDown";
import CopyPricingForm from "./CopyPricingForm";


const SearchPriceLists = () => {
  const text = useTextResources();
  const [hotelsFromApi, setHotelsFromApi] = useState([]);
  const [ratesFromApi, setRatesFromApi] = useState([])
  const [showInActive, setShowInActive] = useState(false);
  const [spec, setSpec] = useState({ InActive: false });
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [selectedForCopy, setSelectedForCopy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const hotels = await Hotels.GetHotels();
        setHotelsFromApi(hotels)
        setSpec((prev) => ({ ...prev, HotelIds: hotels.map(h => h.Id) }))
      } catch (error) {
        onError(error)
      }
    })()

  }, []);

  useEffect(() => {
    (async () => {
      try {
          Rates.GetRates(spec).then(setRatesFromApi);
      } catch (error) {
        onError(error)
      }
    })()
  }, [spec]);

  const getDateCellValue = (row, value) => {
    return `${moment(row[value]).format(dateFormat)}`;
  };

  const hotelFormatter = ({ row }) => {
    return hotelsFromApi?.find((h) => h.Id === row.HotelId)?.Name || '';
  };

  const rateCategoryTypeFormatter = ({ row }) => {
    const typeId = row['RateCategoryId'];
    const rateName = Object.keys(RateCategories).find(
      (key) => RateCategories[key] === typeId
    );
    return rateName ? text(rateName) : '';
  };

  const checkBoxFormatter = ({ value }) => {
    return <Checkbox defaultChecked={value} disabled />;
  };

  const editFormatter = ({ row }) => {
    const hotelName = hotelsFromApi?.find((h) => h.Id === row.HotelId)?.Name || '';
    return (
      <>
      <a
        href={`${window.location.pathname}/Edit?RateId=${row.Id}`}
        style={{ color: 'black' }}
        target='_blank'
        rel='noreferrer'
      >
        <Icon link name='pencil' />
      </a>
        {row.RateCategoryId !== RateCategories.Derived &&
          <button
            type="button"
            aria-label="Copy price list"
            className="pointer"
            style={{ 
              marginLeft: '60px', 
              backgroundColor: 'transparent', 
              border: 'none',
              padding: 0
            }}
            onClick={() => {
              setSelectedForCopy({...row, HotelName: hotelName});
              setOpenCopyModal(true);
            }}
          >
            <Icon link name="copy" />
          </button>}
      </>
    );
  };

  const currencyFormatter = ({ value }) => {
    return Currencies[value]?.code || '';
  }

  const rateCategoryIdPredicate = (RateCategoryId, filter) => {
    const rateName = Object.keys(RateCategories).find(
      (key) => RateCategories[key] === RateCategoryId
    );
    return rateName?.toLocaleLowerCase().includes(filter.value) || null;
  };

  const hotelIdPredicate = (hotelId, filter) => {
    const hotelName = hotelsFromApi.find((h) => h.Id == hotelId).Name;
    return hotelName
      .toLocaleLowerCase()
      .includes(filter.value.toLocaleLowerCase());
  };

  const gridOptions = {
    search: {
      fields: [
        <Form.Field
          key='HotelIds'
          multiple
          clearable
          label={text('Hotel')}
          control={HotelsDropDown}
          data={hotelsFromApi}
          placeholder={'All'}
        />,
        <DatePicker clearable label={text('StartDate')} key='FromDate' />,
        <DatePicker clearable label={text('EndDate')} key='ToDate' />,
        <Form.Field
          key='RateCategoryIds'
          multiple
          clearable
          label={text('RateCategories')}
          control={RateCategoriesDropDown}
          placeholder={'All'}
        />,
        <Form.Field
          key='Ids'
          search={true}
          label={text('RateName')}
          control={RatesDropDown}
          data={ratesFromApi}
          multiple
          clearable
          placeholder={'All'}
        />,
        <Form.Field
          key='PriceGroupIds'
          label={text("PriceGroup")}
          control={PriceGroupsDropDown}
          clearable
          multiple
        />,
        <Form.Field
          key='ClassificationCategoriesIds'
          label={text("PromotionCategory")}
          control={ClassificationCategoriesDropDown}
          multiple clearable search
        />,
        <CheckboxWrapper
          label={text('IncludeInactive')}
          key='InActive'
          onChange={setShowInActive}
        />,
      ],
      api: Rates.GetRates,
      method: 'POST',
      searchOnLoad: true,
      initial: {
        InActive: false,
      },
      spec: {
        InActive: showInActive,
      },
    },
    grid: {
      columns: [
        { name: 'Id', title: text('RateId') },
        { name: 'HotelId', title: text('Hotel'), formatter: hotelFormatter },
        {
          name: 'RateCategoryId',
          title: text('RateCategoryType'),
          formatter: rateCategoryTypeFormatter,
        },
        { name: 'Name', title: text('RateName') },
        { name: 'CurrencyId', title: text('Currency'), formatter: currencyFormatter },
        {
          name: 'StartDate',
          title: text('StartDate'),
          getCellValue: getDateCellValue,
        },
        {
          name: 'EndDate',
          title: text('EndDate'),
          getCellValue: getDateCellValue,
        },
        {
          name: 'IsDistributeToChannelManager',
          title: text('Distribute'),
          formatter: checkBoxFormatter,
          customFilter: CheckBoxToggle,
        },
        {
          name: 'IsActive',
          title: text('Active'),
          formatter: checkBoxFormatter,
          customFilter: CheckBoxToggle,
        },
        {
          name: 'Edit',
          title: text('Edit'),
          formatter: editFormatter,
        },
      ],
      paging: true,
      filtering: {
        extensions: [
          { columnName: 'Edit', filteringEnabled: false },
          {
            columnName: 'RateCategoryId',
            predicate: rateCategoryIdPredicate,
          },
          {
            columnName: 'HotelId',
            predicate: hotelIdPredicate,
          },
        ],
        defaults: [
          { columnName: 'IsActive', value: true },
          { columnName: 'IsDistributeToChannelManager', value: true },
        ],
      },
      menu: [],
      cacheId: 'PriceList',
      customOptions: {
        overrideData: true,
        sort: {
          sorting: [
            { columnName: 'Hotel', direction: 'asc' },
            { columnName: 'RateCategoryId', direction: 'asc' },
          ],
        },
      },
      editable: {
        allowAdd: true,
        allowEdit: false,
        modalEditor: {
          modal: PriceListCategorySelectionPopup,
        },
      },
    },
  }
  
  const onCloseCopyModal = () => {
    setOpenCopyModal(false);
    setSelectedForCopy(null);
  };

  return (
    <>
      <GridSearchTemplate options={gridOptions} onSearchChange={(data) => setSpec({ ...data, Ids: [] })} />
      <CopyPricingForm 
        open={openCopyModal} 
        onClose={onCloseCopyModal} 
        rate={selectedForCopy}
      />
    </>
  );
};
export default SearchPriceLists;
